import { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CalendarDays, Globe, Flag, Lock, Search, X, Users, LayoutGrid, ArrowLeft, ShieldAlert, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { RansomwareItem } from '@/types/ransomware';
import { CountryFilter } from '@/components/CountryFilter';
import WorldMap from '@/components/WorldMap';
import { RansomwareStats } from '@/components/RansomwareStats';
import { Input } from '@/components/ui/input';
import { useParams, useNavigate } from 'react-router-dom';
import { RansomwareGroups } from '@/components/ransomware/RansomwareGroups';
import { getCountryISO2 } from '@/utils/countryMapping';
import { RansomwareGroupDetail } from '@/components/ransomware/RansomwareGroupDetail';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Slider } from '@/components/ui/slider';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

// Items per page
const ITEMS_PER_PAGE = 12;

// Flag emojis for common countries
const countryFlags: Record<string, string> = {
  'US': '🇺🇸', 'UK': '🇬🇧', 'CA': '🇨🇦', 'AU': '🇦🇺', 'DE': '🇩🇪',
  'FR': '🇫🇷', 'IT': '🇮🇹', 'ES': '🇪🇸', 'JP': '🇯🇵', 'CN': '🇨🇳',
  'RU': '🇷🇺', 'BR': '🇧🇷', 'IN': '🇮🇳', 'PL': '🇵🇱',
};

type ViewMode = 'victims' | 'criminals';

export function RansomwareFeed() {
  const { language, t } = useLanguage();
  const { countryCode, groupName } = useParams<{ countryCode: string; groupName: string }>();
  const navigate = useNavigate();
  const [ransomwareData, setRansomwareData] = useState<RansomwareItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('victims');

  // Visibility state
  const [showMap, setShowMap] = useState(true);
  const [showStats, setShowStats] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Country filtering
  const [excludedCountries, setExcludedCountries] = useState<string[]>([]);
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  // Search functionality
  const [searchQuery, setSearchQuery] = useState('');

  // Sync selectedCountry with URL param
  useEffect(() => {
    if (countryCode) {
      setSelectedCountry(countryCode.toUpperCase());
    } else {
      setSelectedCountry(null);
    }
  }, [countryCode]);

  // Timeline State
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Group Data (filtered by group name only)
  const groupData = useMemo(() => {
    return groupName ? ransomwareData.filter(item => item.group === groupName) : [];
  }, [ransomwareData, groupName]);

  // Calculate min/max year for the group
  const { minYear, maxYear } = useMemo(() => {
    if (groupData.length === 0) return { minYear: new Date().getFullYear(), maxYear: new Date().getFullYear() };
    const years = groupData.map(item => new Date(item.attackdate).getFullYear());
    return {
      minYear: Math.min(...years),
      maxYear: Math.max(...years)
    };
  }, [groupData]);

  // Reset selectedYear when group changes
  useEffect(() => {
    if (groupName) {
      setSelectedYear(maxYear);
    } else {
      setSelectedYear(null);
    }
  }, [groupName, maxYear]);

  // Data to process for stats and map (filtered by year if group selected)
  const dataToProcess = useMemo(() => {
    let data = groupName ? groupData : ransomwareData;
    if (groupName && selectedYear) {
      data = data.filter(item => new Date(item.attackdate).getFullYear() <= selectedYear);
    }
    return data;
  }, [groupName, groupData, ransomwareData, selectedYear]);

  // Handle map selection
  const handleMapSelect = (country: string | null) => {
    if (country) {
      navigate(`/ransomware/${country.toLowerCase()}`);
    } else {
      navigate('/ransomware');
    }
    setCurrentPage(1);
  };

  // Fetch Ransomware data from Supabase
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: downloadError } = await supabase
          .storage
          .from('dane')
          .download('ransomware.json');

        if (downloadError) {
          console.error('Error fetching ransomware data:', downloadError);
          throw new Error(`Failed to fetch ransomware data: ${downloadError.message}`);
        }

        if (!data) {
          console.error('No ransomware data returned');
          throw new Error('No ransomware data returned');
        }

        // Parse the JSON data from the storage blob
        const text = await data.text();
        const jsonData = JSON.parse(text);

        // Sort by attack date (newest first)
        const sortedData = jsonData.sort((a: RansomwareItem, b: RansomwareItem) =>
          new Date(b.attackdate).getTime() - new Date(a.attackdate).getTime()
        );

        // Extract unique countries - Fix the type issue by ensuring countries are strings
        const countries = Array.from(
          new Set(sortedData.map((item: RansomwareItem) => item.country || ""))
        ).map(country => String(country)) // Explicitly convert to string
          .sort((a, b) => a.localeCompare(b)); // Now TypeScript knows this is a string

        setAvailableCountries(countries); // Now countries is explicitly a string array
        setRansomwareData(sortedData);
        setLoading(false);
      } catch (err) {
        console.error('Error in ransomware data fetch:', err);
        setError('Failed to load ransomware data');
        setLoading(false);

        toast({
          title: "Error loading ransomware data",
          description: "Could not load the ransomware data. Please try again later.",
          variant: "destructive",
        });
      }
    };

    fetchData();
  }, [toast]);


  // ... imports

  // ... inside component

  // Calculate map stats
  const countryStats = useMemo(() => {
    const stats: Record<string, { count: number; hasNewAttacks: boolean }> = {};

    // Filter data if group is selected (already filtered in dataToProcess)
    const data = dataToProcess;

    if (dataToProcess.length === 0) return stats;

    // Use current date for "new attacks" check
    const today = new Date();

    dataToProcess.forEach(item => {
      const country = item.country;
      if (!country) return;

      // Normalize to ISO2
      const iso2 = getCountryISO2(country);
      if (!iso2) return;

      if (!stats[iso2]) {
        stats[iso2] = { count: 0, hasNewAttacks: false };
      }

      stats[iso2].count += 1;

      // Check if this attack is from today
      const attackDate = new Date(item.attackdate);
      if (
        attackDate.getFullYear() === today.getFullYear() &&
        attackDate.getMonth() === today.getMonth() &&
        attackDate.getDate() === today.getDate()
      ) {
        stats[iso2].hasNewAttacks = true;
      }
    });

    return stats;
  }, [dataToProcess]);



  // Handle toggling a country's inclusion in the filter
  const handleToggleCountry = (country: string) => {
    setExcludedCountries(prev => {
      if (prev.includes(country)) {
        return prev.filter(c => c !== country);
      } else {
        return [...prev, country];
      }
    });
    // Reset to first page when filter changes
    setCurrentPage(1);
  };

  // Handle select all countries
  const handleSelectAll = () => {
    setExcludedCountries([]);
    handleMapSelect(null);
    setCurrentPage(1);
  };

  // Handle deselect all countries
  const handleDeselectAll = () => {
    setExcludedCountries([...availableCountries]);
    handleMapSelect(null);
    setCurrentPage(1);
  };

  // Clear search query
  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Filter data based on excluded countries and search query
  const filteredData = useMemo(() => {
    let filtered = ransomwareData;

    // Apply country filter
    if (selectedCountry) {
      filtered = filtered.filter(item => item.country === selectedCountry);
    } else if (excludedCountries.length > 0) {
      filtered = filtered.filter(item => !excludedCountries.includes(item.country));
    }

    // Apply search filter if search query exists
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item =>
        (item.victim && item.victim.toLowerCase().includes(query)) ||
        (item.domain && item.domain.toLowerCase().includes(query)) ||
        (item.group && item.group.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [ransomwareData, excludedCountries, selectedCountry, searchQuery]);

  // Update total pages when filtered data changes
  useEffect(() => {
    setTotalPages(Math.ceil(filteredData.length / ITEMS_PER_PAGE));
  }, [filteredData]);

  // Format date properly for both English and Polish
  const formatDate = (dateString: string) => {
    try {
      // First parse the date
      const date = new Date(dateString);

      // Then format according to language
      if (language === 'en') {
        return format(date, 'PPP'); // Long localized date in English
      } else {
        // Format for Polish - e.g., "4 Marca 2025"
        return format(date, 'd MMMM yyyy', { locale: pl });
      }
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  // Get current page data
  const getCurrentItems = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredData.slice(startIndex, endIndex);
  };

  // Generate pagination links
  const getPaginationItems = () => {
    const items = [];

    // Always show first page
    items.push(
      <PaginationItem key="first">
        <PaginationLink
          onClick={() => setCurrentPage(1)}
          isActive={currentPage === 1}
        >
          1
        </PaginationLink>
      </PaginationItem>
    );

    // Add ellipsis if needed
    if (currentPage > 3) {
      items.push(
        <PaginationItem key="ellipsis1">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    // Add pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (i <= 1 || i >= totalPages) continue;
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => setCurrentPage(i)}
            isActive={currentPage === i}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Add ellipsis if needed
    if (currentPage < totalPages - 2) {
      items.push(
        <PaginationItem key="ellipsis2">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    // Always show last page if there is more than one page
    if (totalPages > 1) {
      items.push(
        <PaginationItem key="last">
          <PaginationLink
            onClick={() => setCurrentPage(totalPages)}
            isActive={currentPage === totalPages}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

  // Get localized country name
  const getCountryName = (code: string) => {
    try {
      const regionNames = new Intl.DisplayNames([language], { type: 'region' });
      return regionNames.of(code) || code;
    } catch (e) {
      return code;
    }
  };

  // Get flag emoji for a country code
  const getCountryFlag = (countryCode: string) => {
    return countryFlags[countryCode] || '🏳️';
  };

  return (
    <section className="py-20 bg-secondary/50 dark:bg-secondary/20">
      <div className="container px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 relative">
          {(groupName || selectedCountry) && (
            <Button
              variant="ghost"
              className="absolute left-0 top-0 gap-2"
              onClick={() => navigate('/ransomware')}
            >
              <ArrowLeft className="h-4 w-4" />
              {language === 'en' ? 'Back to Feed' : 'Wróć do listy'}
            </Button>
          )}
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-foreground">
            {groupName
              ? groupName
              : selectedCountry
                ? getCountryName(selectedCountry)
                : t('ransomware.title')}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {groupName
              ? (language === 'en' ? `Detailed analysis of ${groupName} activity` : `Szczegółowa analiza aktywności ${groupName}`)
              : selectedCountry
                ? (language === 'en' ? `Ransomware attacks in ${getCountryName(selectedCountry)}` : `Ataki ransomware w: ${getCountryName(selectedCountry)}`)
                : t('ransomware.subtitle')}
          </p>
          <Separator className="mt-4 mb-8 mx-auto w-24 bg-primary/50" />
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              <p className="mt-4 text-muted-foreground">{t('ransomware.loading')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-10">
            <p className="text-destructive">{t('ransomware.error')}: {error}</p>
          </div>
        ) : (
          <>
            {/* World Map */}
            <div className="relative mb-8 group">
              <div className="absolute right-4 top-4 z-10">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-background/80 backdrop-blur-sm"
                  onClick={() => setShowMap(!showMap)}
                >
                  {showMap ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                  {showMap
                    ? (language === 'en' ? 'Hide Map' : 'Ukryj mapę')
                    : (language === 'en' ? 'Show Map' : 'Pokaż mapę')}
                </Button>
              </div>

              {showMap ? (
                <WorldMap
                  countryStats={countryStats}
                  selectedCountry={selectedCountry}
                  onSelectCountry={handleMapSelect}
                />
              ) : (
                <div className="h-24 flex items-center justify-center border rounded-lg bg-muted/10">
                  <p className="text-muted-foreground text-sm">
                    {language === 'en' ? 'Map hidden' : 'Mapa ukryta'}
                  </p>
                </div>
              )}
            </div>

            {/* Timeline Slider for Group View */}
            {groupName && showMap && (
              <div className="mb-8 px-4 py-6 bg-card/50 rounded-lg border backdrop-blur-sm">
                <div className="flex flex-col space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-medium text-muted-foreground">
                      {language === 'en' ? 'Timeline (Cumulative)' : 'Oś czasu (Narastająco)'}
                    </h3>
                    <span className="text-lg font-bold text-red-600">
                      {minYear} - {selectedYear}
                    </span>
                  </div>
                  <Slider
                    defaultValue={[maxYear]}
                    value={[selectedYear || maxYear]}
                    min={minYear}
                    max={maxYear}
                    step={1}
                    onValueChange={(value) => setSelectedYear(value[0])}
                    className="w-full [&_.bg-primary]:bg-red-600 [&_.border-primary]:border-red-600"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground px-1">
                    {Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i).map((year) => (
                      <span
                        key={year}
                        className={cn(
                          "transition-colors duration-200",
                          year === selectedYear ? "text-red-600 font-bold" : ""
                        )}
                      >
                        {year}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Statistics Charts */}
            <div className="relative mb-8 group">
              <div className="absolute right-4 top-4 z-10">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-background/80 backdrop-blur-sm"
                  onClick={() => setShowStats(!showStats)}
                >
                  {showStats ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                  {showStats
                    ? (language === 'en' ? 'Hide Stats' : 'Ukryj statystyki')
                    : (language === 'en' ? 'Show Stats' : 'Pokaż statystyki')}
                </Button>
              </div>

              {showStats ? (
                <RansomwareStats
                  data={dataToProcess}
                  selectedGroup={groupName}
                  selectedCountry={selectedCountry}
                />
              ) : (
                <div className="h-24 flex items-center justify-center border rounded-lg bg-muted/10">
                  <p className="text-muted-foreground text-sm">
                    {language === 'en' ? 'Statistics hidden' : 'Statystyki ukryte'}
                  </p>
                </div>
              )}
            </div>

            {/* View Toggle - Hide if group is selected */}
            {!groupName && (
              <div className="flex justify-center mb-8">
                <div className="bg-background/50 p-1 rounded-lg border inline-flex">
                  <Button
                    variant={viewMode === 'victims' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('victims')}
                    className="gap-2"
                  >
                    <LayoutGrid className="h-4 w-4" />
                    {language === 'en' ? 'Victims' : 'Ofiary'}
                  </Button>
                  <Button
                    variant={viewMode === 'criminals' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('criminals')}
                    className="gap-2"
                  >
                    <Users className="h-4 w-4" />
                    {language === 'en' ? 'Criminals' : 'Przestępcy'}
                  </Button>
                </div>
              </div>
            )}

            {groupName ? (
              <RansomwareGroupDetail
                groupName={groupName}
                ransomwareData={dataToProcess}
              />
            ) : viewMode === 'criminals' ? (
              <RansomwareGroups data={ransomwareData} />
            ) : (
              <>
                {/* Search bar */}
                <div className="relative mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1); // Reset to first page on search
                      }}
                      placeholder={t('ransomware.searchPlaceholder')}
                      className="pl-10 pr-10"
                    />
                    {searchQuery && (
                      <button
                        onClick={handleClearSearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {searchQuery && (
                    <div className="mt-2 text-sm text-muted-foreground">
                      {filteredData.length === 0
                        ? t('ransomware.noSearchResults')
                        : t('ransomware.searchResults', { count: filteredData.length })}
                    </div>
                  )}
                </div>

                {/* Country filter - Only show if no country selected on map */}
                {!selectedCountry && (
                  <CountryFilter
                    countries={availableCountries}
                    excludedCountries={excludedCountries}
                    onToggleCountry={handleToggleCountry}
                    onSelectAll={handleSelectAll}
                    onDeselectAll={handleDeselectAll}
                  />
                )}

                {selectedCountry && (
                  <div className="mb-6 flex items-center justify-center">
                    <div className="bg-primary/10 text-primary px-4 py-2 rounded-full flex items-center gap-2">
                      <span className="font-medium">
                        {t('ransomware.showingResultsFor') || 'Pokazywanie wyników dla'}: {selectedCountry}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 rounded-full hover:bg-primary/20"
                        onClick={() => handleMapSelect(null)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}

                {filteredData.length === 0 ? (
                  <div className="text-center py-20">
                    {selectedCountry ? (
                      <div className="space-y-4">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
                          <ShieldAlert className="h-10 w-10 text-green-600 dark:text-green-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-foreground">
                          {getCountryName(selectedCountry)}
                        </h3>
                        <p className="text-lg text-muted-foreground max-w-md mx-auto">
                          {language === 'en'
                            ? `Great news! No ransomware attacks have been recorded in ${getCountryName(selectedCountry)}.`
                            : `Świetne wieści! Nie odnotowano żadnych ataków ransomware w kraju: ${getCountryName(selectedCountry)}.`}
                        </p>
                        <div className="flex justify-center gap-4 mt-6">
                          <div className="text-center p-4 bg-card border rounded-lg">
                            <div className="text-3xl font-bold text-green-500">0</div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">
                              {language === 'en' ? 'Attacks' : 'Ataki'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-lg text-muted-foreground">
                          {searchQuery
                            ? t('ransomware.noSearchResults')
                            : t('ransomware.noFilteredResults')}
                        </p>
                        <Button
                          variant="outline"
                          className="mt-4"
                          onClick={() => {
                            handleSelectAll();
                            handleClearSearch();
                          }}
                        >
                          {t('ransomware.resetFilters')}
                        </Button>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {getCurrentItems().map((item, index) => (
                        <Card
                          key={`${item.domain}-${index}`}
                          className={cn(
                            "glass-card overflow-hidden transition-all duration-500 hover:shadow-xl hover:-translate-y-1",
                            "animate-scale-in"
                          )}
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <CardContent className="p-6">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center">
                                <Lock className="h-4 w-4 text-red-600 mr-1" />
                                <span className="text-xs font-semibold text-red-600">
                                  {item.group}
                                </span>
                              </div>
                              <div className="flex items-center text-xs text-muted-foreground">
                                <CalendarDays className="h-3 w-3 mr-1" />
                                {formatDate(item.attackdate)}
                              </div>
                            </div>

                            <h3 className="text-lg font-semibold mb-2">{item.victim}</h3>

                            <div className="flex items-center mb-4 text-sm text-muted-foreground">
                              <Globe className="h-4 w-4 mr-1" />
                              <span className="truncate">
                                {(() => {
                                  try {
                                    const url = new URL(item.domain.startsWith('http') ? item.domain : `http://${item.domain}`);
                                    return url.hostname;
                                  } catch {
                                    return item.domain?.split('/')[0] || '';
                                  }
                                })()}
                              </span>
                            </div>

                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center">
                                <Flag className="h-4 w-4 mr-1" />
                                <span className="text-sm font-medium">
                                  {getCountryFlag(item.country)} {item.country || t('ransomware.other')}
                                </span>
                              </div>

                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full"
                                onClick={() => navigate(`/ransomware/group/${encodeURIComponent(item.group)}`)}
                              >
                                {t('ransomware.details')}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="mt-12">
                        <Pagination className="justify-center">
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                aria-disabled={currentPage === 1}
                                className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                              />
                            </PaginationItem>

                            {getPaginationItems()}

                            <PaginationItem>
                              <PaginationNext
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                aria-disabled={currentPage === totalPages}
                                className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                        <div className="text-center text-sm text-muted-foreground mt-2">
                          {t('news.page')} {currentPage} {t('news.of')} {totalPages}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
